import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useGPS } from '../../contexts/GPSContext';
import StatTile from '../ui/StatTileNew';
import StatCard from '../ui/StatCard';
import SortableTable from '../SortableTable';
import { safeParseDate } from '../../utils/dateUtils';
import { normalizeCost } from './gpsCostUtils';
import { isResolvedIssue } from '../../utils/gpsStatusCategories';

const formatCurrency = (value) => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
}).format(value || 0);

const formatNumber = (value) => new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 2,
}).format(value || 0);

const getCostRows = (issues = []) => issues
  .map(issue => ({
    ...issue,
    costValue: normalizeCost(issue?.cost),
  }))
  .filter(issue => issue.costValue !== null);

const isDateInRange = (dateValue, startDate, endDate) => {
  const date = safeParseDate(dateValue);
  if (!date || !startDate || !endDate) return false;

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  return date >= start && date <= end;
};

const getCompletedCostRows = (issues = [], startDate, endDate) => getCostRows(issues)
  .filter(issue => isResolvedIssue(issue) && isDateInRange(issue.status_date, startDate, endDate));

const getCostStats = (issues = [], startDate, endDate) => {
  const rows = getCompletedCostRows(issues, startDate, endDate);
  const total = rows.reduce((sum, issue) => sum + issue.costValue, 0);
  const average = rows.length > 0 ? total / rows.length : 0;
  const sortedCosts = rows.map(issue => issue.costValue).sort((a, b) => a - b);
  const median = sortedCosts.length === 0
    ? 0
    : sortedCosts.length % 2 === 0
      ? (sortedCosts[sortedCosts.length / 2 - 1] + sortedCosts[sortedCosts.length / 2]) / 2
      : sortedCosts[Math.floor(sortedCosts.length / 2)];

  return {
    rows,
    total,
    average,
    median,
    count: rows.length,
  };
};

const getVariation = (current, previous) => {
  if (previous === null || previous === undefined) return null;
  if (previous === 0) return current > 0 ? '+∞' : '0.0%';

  const variation = ((current - previous) / Math.abs(previous)) * 100;
  return `${variation > 0 ? '+' : ''}${variation.toFixed(1)}%`;
};

const getCurrencyDifference = (current, previous) => {
  if (previous === null || previous === undefined) return null;
  const difference = current - previous;
  return `${difference > 0 ? '+' : ''}${formatCurrency(difference)}`;
};

const getNumberDifference = (current, previous) => {
  if (previous === null || previous === undefined) return null;
  const difference = current - previous;
  return `${difference > 0 ? '+' : ''}${formatNumber(difference)}`;
};

const buildDistribution = (rows) => {
  const map = new Map();

  rows.forEach(issue => {
    const roundedCost = Math.round(issue.costValue * 100) / 100;
    const existing = map.get(roundedCost) || { cost: roundedCost, count: 0, total: 0 };
    existing.count += 1;
    existing.total += issue.costValue;
    map.set(roundedCost, existing);
  });

  return Array.from(map.values())
    .sort((a, b) => a.cost - b.cost)
    .map(item => ({
      ...item,
      label: formatCurrency(item.cost),
    }));
};

const buildSectorStats = (rows) => {
  const map = new Map();

  rows.forEach(issue => {
    const sector = issue.sector || 'Non spécifié';
    const existing = map.get(sector) || { sector, count: 0, total: 0 };
    existing.count += 1;
    existing.total += issue.costValue;
    map.set(sector, existing);
  });

  return Array.from(map.values())
    .map(item => ({
      ...item,
      average: item.count > 0 ? item.total / item.count : 0,
    }))
    .sort((a, b) => b.total - a.total);
};

const EuroIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10h12M4 14h10M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12a7.9 7.9 0 0 0 7.8 8A7.7 7.7 0 0 0 19 18" />
  </svg>
);

const CostTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-900">
      <div className="font-medium text-gray-900 dark:text-white">{label}</div>
      <div className="text-gray-600 dark:text-gray-300">{item.count} ticket{item.count > 1 ? 's' : ''}</div>
      <div className="text-gray-600 dark:text-gray-300">Total HT: {formatCurrency(item.total)}</div>
    </div>
  );
};

const GPSCosts = () => {
  const gps = useGPS();

  const currentStats = useMemo(
    () => getCostStats(gps.issues, gps.startDate, gps.endDate),
    [gps.issues, gps.startDate, gps.endDate]
  );
  const previousStats = useMemo(
    () => getCostStats(gps.previousIssues, gps.previousStartDate, gps.previousEndDate),
    [gps.previousIssues, gps.previousStartDate, gps.previousEndDate]
  );
  const distribution = useMemo(() => buildDistribution(currentStats.rows), [currentStats.rows]);
  const sectorStats = useMemo(() => buildSectorStats(currentStats.rows), [currentStats.rows]);

  const sectorColumns = useMemo(() => [
    {
      key: 'sector',
      label: 'Secteur',
      render: (row) => (
        <span className="font-medium text-gray-800 dark:text-gray-200">
          {row.sector}
        </span>
      ),
    },
    {
      key: 'total',
      label: 'Total HT',
      render: (row) => (
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          {formatCurrency(row.total)}
        </span>
      ),
      rawValue: row => row.total,
      className: 'text-right',
    },
    {
      key: 'average',
      label: 'Moyenne',
      render: (row) => (
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          {formatCurrency(row.average)}
        </span>
      ),
      rawValue: row => row.average,
      className: 'text-right',
    },
    {
      key: 'count',
      label: 'Tickets',
      render: (row) => (
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          {row.count.toLocaleString('fr-FR')}
        </span>
      ),
      className: 'text-right',
    },
  ], []);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 rounded-xl border border-[#cfe0fb] dark:border-blue-900 bg-[#f5f9ff] dark:bg-blue-950/30 px-4 py-2.5 text-xs text-[#3d5a8a] dark:text-blue-200">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0">
          <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
        </svg>
        <span>Les coûts portent uniquement sur les <strong>tickets résolus</strong> (Terminé(e), Terminé ou Approuvé) de la période. Le filtre « Afficher » est sans effet sur cet onglet.</span>
      </div>
      <StatCard>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatTile
            label="Total facturé HT"
            value={formatCurrency(currentStats.total)}
            previousValue={formatCurrency(previousStats.total)}
            variation={getVariation(currentStats.total, previousStats.total)}
            difference={getCurrencyDifference(currentStats.total, previousStats.total)}
            icon={<EuroIcon />}
            color="green"
            description="Somme des coûts renseignés sur les tickets terminés dans la période filtrée."
            comparisonLabel="vs la période précédente"
          />
          <StatTile
            label="Prix moyen HT / ticket"
            value={formatCurrency(currentStats.average)}
            previousValue={formatCurrency(previousStats.average)}
            variation={getVariation(currentStats.average, previousStats.average)}
            difference={getCurrencyDifference(currentStats.average, previousStats.average)}
            icon={<EuroIcon />}
            color="blue"
            description="Moyenne des coûts renseignés sur les tickets terminés dans la période filtrée."
            comparisonLabel="vs la période précédente"
          />
          <StatTile
            label="Prix médian HT"
            value={formatCurrency(currentStats.median)}
            previousValue={formatCurrency(previousStats.median)}
            variation={getVariation(currentStats.median, previousStats.median)}
            difference={getCurrencyDifference(currentStats.median, previousStats.median)}
            icon={<EuroIcon />}
            color="purple"
            description="Prix médian des tickets terminés avec un coût renseigné."
            comparisonLabel="vs la période précédente"
          />
          <StatTile
            label="Tickets valorisés"
            value={currentStats.count.toLocaleString('fr-FR')}
            previousValue={previousStats.count.toLocaleString('fr-FR')}
            variation={getVariation(currentStats.count, previousStats.count)}
            difference={getNumberDifference(currentStats.count, previousStats.count)}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            }
            color="orange"
            description="Nombre de tickets terminés avec un coût exploitable."
            comparisonLabel="vs la période précédente"
          />
        </div>
      </StatCard>

      <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Répartition des prix HT
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nombre de tickets terminés par montant facturé.
            </p>
          </div>
          <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
            {distribution.length} prix distinct{distribution.length > 1 ? 's' : ''}
          </span>
        </div>

        {distribution.length === 0 ? (
          <div className="flex h-[320px] items-center justify-center text-center text-gray-600 dark:text-gray-400">
            Aucun ticket terminé avec coût renseigné sur la période filtrée.
          </div>
        ) : (
          <div className="h-[360px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distribution} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  stroke="#9CA3AF"
                  axisLine={false}
                  tickLine={false}
                  angle={-35}
                  textAnchor="end"
                  height={70}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  stroke="#9CA3AF"
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CostTooltip />} />
                <Bar dataKey="count" name="Tickets" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Coûts par secteur
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Classement des secteurs par total facturé HT sur les tickets terminés.
          </p>
        </div>

        <SortableTable
          data={sectorStats}
          columns={sectorColumns}
          emptyMessage="Aucun ticket terminé avec coût renseigné par secteur."
          exportable
          exportFilename="gps-couts-par-secteur.csv"
          disableFilters={true}
          initialSortBy={{ key: 'total', direction: 'descending' }}
        />
      </div>
    </div>
  );
};

export default GPSCosts;
